import { Alert, AlertDescription, Button, Checkbox, Label } from '@monksflow/monks-ui';
import { AlertCircleIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { ALL_DAYS, DAYS_OF_WEEK, useScheduleSummary, type ScheduleValue } from './scheduleValue';

interface FieldProps {
  value: ScheduleValue;
  onChange: (value: ScheduleValue) => void;
  disabled?: boolean;
}

/**
 * The two halves of a run schedule are exported separately because the surfaces that show them
 * disagree on layout: the organization page puts each behind its own accordion item, while a
 * workspace row shows both flat under a single save.
 */
export const ScheduleEnabledField = ({ value, onChange, disabled = false }: FieldProps) => {
  const { t } = useTranslation('admin');

  return (
    <div className="flex flex-col gap-2xs">
      <div className="flex items-center gap-xs">
        <Checkbox
          id="schedule-enabled"
          checked={value.enabled}
          disabled={disabled}
          onCheckedChange={(checked) => onChange({ ...value, enabled: checked === true })}
        />
        <Label htmlFor="schedule-enabled" className="cursor-pointer font-normal">
          {t('admin.scheduleEditor.enableAutomatedOpportunityDiscovery')}
        </Label>
      </div>
      <p className="text-body-sm text-muted-foreground">
        {t('admin.scheduleEditor.whenEnabledTheSystemWill')}
      </p>
    </div>
  );
};

export const ScheduleDaysField = ({ value, onChange, disabled = false }: FieldProps) => {
  const { t } = useTranslation('admin');

  const selectedDays = value.daysOfWeek;
  const daysDisabled = disabled || !value.enabled;

  const setDays = (daysOfWeek: number[]) => onChange({ ...value, daysOfWeek });

  const toggleDay = (day: number) =>
    setDays(
      selectedDays.includes(day)
        ? selectedDays.filter((selected) => selected !== day)
        : [...selectedDays, day].sort(),
    );

  return (
    <div className="flex flex-col gap-md">
      <div className="flex items-center justify-between gap-md">
        <p className="text-body-sm text-muted-foreground">
          {t('admin.scheduleEditor.selectWhichDaysOpportunitiesShould')}
        </p>
        <div className="flex gap-xs">
          <Button
            type="button"
            size="sm"
            variant="outline"
            onClick={() => setDays([...ALL_DAYS].sort())}
            disabled={daysDisabled || selectedDays.length === ALL_DAYS.length}
          >
            {t('admin.scheduleEditor.selectAll')}
          </Button>
          <Button
            type="button"
            size="sm"
            variant="outline"
            onClick={() => setDays([])}
            disabled={daysDisabled || selectedDays.length === 0}
          >
            {t('admin.scheduleEditor.clearAll')}
          </Button>
        </div>
      </div>

      <div className="flex flex-col gap-2xs">
        {DAYS_OF_WEEK.map((day) => {
          const inputId = `schedule-day-${day.value}`;
          const isSelected = selectedDays.includes(day.value);
          return (
            <Label
              key={day.value}
              htmlFor={inputId}
              className={`flex cursor-pointer items-center gap-xs rounded-md border p-sm font-normal transition-colors duration-fast ease-out ${
                isSelected ? 'border-primary bg-primary/5' : 'border-border hover:bg-accent/50'
              } ${daysDisabled ? 'pointer-events-none opacity-50' : ''}`}
            >
              <Checkbox
                id={inputId}
                checked={isSelected}
                disabled={daysDisabled}
                onCheckedChange={() => toggleDay(day.value)}
              />
              {t(day.labelKey)}
            </Label>
          );
        })}
      </div>

      {value.enabled && selectedDays.length === 0 && (
        <Alert icon={<AlertCircleIcon />}>
          <AlertDescription>
            {t('admin.scheduleEditor.noDaysSelectedOpportunitiesWill')}
          </AlertDescription>
        </Alert>
      )}
    </div>
  );
};

/** Both halves, flat. Used where a single save covers the schedule alongside other fields. */
export const ScheduleFields = ({ value, onChange, disabled = false }: FieldProps) => {
  const summary = useScheduleSummary(value);

  return (
    <div className="flex flex-col gap-md">
      <ScheduleEnabledField value={value} onChange={onChange} disabled={disabled} />
      <ScheduleDaysField value={value} onChange={onChange} disabled={disabled} />
      {summary && <p className="text-body-sm text-muted-foreground">{summary}</p>}
    </div>
  );
};
